import { useSynchronousDropzone, getFirstValidationError, IsMinImageDimensions } from '@nike/asset-delivery-distributed-assets-upload-component';
import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, {
 useState, useEffect, useCallback, useRef
} from 'react';

import { getOktaToken } from '../../../utils/service-calls/sls';
import { defaultNFSImageUrl, defaultNSPImageUrl } from '../../../utils/static/sls-property-values';
import { ButtonWhite } from '../../reusable';

import {
  containerStyle,
  headerStyle,
  subtextStyle,
  titleStyle,
  buttonContainerStyle,
  hiddenInputStyle,
  imagePreviewContainerStyle,
  imagePreviewImageStyle,
  imagePreviewLeftStyle,
  imagePreviewRightStyle,
  imageUrlInputStyle,
  inputLabelStyle,
} from './styles';

// Store image size
const MIN_IMAGE_HEIGHT = 700;
const MIN_IMAGE_WIDTH = 1344;

const replaceDefaultWithAutoTransform = (url) => url.replace('t_default', 'f_auto');

const StoreImage = ({
  accessToken,
  imageUrl,
  selectedStore,
  storeData,
  username,
  onChange,
}) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadState, setUploadState] = useState({
    token: '',
    uploadError: '',
    uploadSuccess: false,
  });
  const prevImageUrlRef = useRef(null);

  // Show loading state when imageUrl changes to a different value
  useEffect(() => {
    if (imageUrl && imageUrl !== prevImageUrlRef.current) {
      setImageLoading(true);
      setImageError(false);
    }
    prevImageUrlRef.current = imageUrl;
  }, [imageUrl]);

  const onError = useCallback(
    (errors) => {
      // eslint-disable-next-line no-console
      console.error(errors);
      setIsUploading(false);
      const validationError = getFirstValidationError(errors);
      if (validationError) {
        setUploadState((prev) => ({ ...prev, uploadError: validationError.message, uploadSuccess: false }));
      } else {
        setUploadState((prev) => ({ ...prev, uploadError: 'Failed to upload', uploadSuccess: false }));
      }
    },
    [],
  );

  const onStartUploading = useCallback(() => {
    setIsUploading(true);
    setUploadState((prev) => ({ ...prev, uploadError: '', uploadSuccess: false }));
  }, []);

  const onStopUploading = useCallback(
    (results) => {
      setIsUploading(false);
      const result = results?.[0];
      if (!result || !result.data) {
        return;
      }
      const newImageUrl = replaceDefaultWithAutoTransform(result.data.externalUrl);
      onChange(newImageUrl);
      setUploadState((prev) => ({ ...prev, uploadError: '', uploadSuccess: true }));

      // Check for duplicate asset
      if (result.isDuplicate && result.message) {
        setUploadState((prev) => ({ ...prev, uploadError: 'Image already exists. Using existing Image URL' }));
      }
    },
    [onChange],
  );

  const { getInputProps, open } = useSynchronousDropzone({
    authToken: uploadState.token,
    createdBy: username,
    envName: process.env.NODE_ENV.toLowerCase() === 'production' ? 'prod' : 'test',
    onError,
    onStartUploading,
    onStopUploading,
    validator: [IsMinImageDimensions(MIN_IMAGE_WIDTH, MIN_IMAGE_HEIGHT)],
  });

  // Get Okta token for file upload
  useEffect(() => {
    getOktaToken(accessToken)
      .then(({ body }) => setUploadState((state) => ({ ...state, token: body })))
      .catch(({ message }) => setUploadState((state) => ({ ...state, uploadError: message })));
  }, [accessToken]);

  const handleImageUrlChange = (e) => {
    const newUrl = e.target.value;
    onChange(newUrl);
  };

  const handleDefaultImage = () => {
    const defaultImage = storeData.facilityType === 'NIKE_OWNED_STORE' && ['FACTORY', 'CLEARANCE', 'COMMUNITY']
      .includes(storeData.businessConcept)
      ? defaultNFSImageUrl
      : defaultNSPImageUrl;
    onChange(defaultImage);
    setUploadState({
      ...uploadState,
      uploadError: '',
      uploadSuccess: false,
    });
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={titleStyle}>Store Image</h2>
        <span style={subtextStyle}>Image shown in Google</span>
      </div>
      <div style={imagePreviewContainerStyle}>
        {/* Left side - Image Preview */}
        <div style={imagePreviewLeftStyle}>
          {!imageUrl && (
            <p className="body-2 text-color-secondary">No image URL provided</p>
          )}
          {imageUrl && imageLoading && !imageError && (
            <div>
              <Loading />
              <p className="body-2 mt2-sm">Loading image...</p>
            </div>
          )}
          {imageUrl && imageError && (
            <p className="body-2 text-color-error">Image Unavailable</p>
          )}
          {imageUrl && !imageError && (
            <img
              key={imageUrl}
              alt={`${selectedStore.label} store preview`}
              src={imageUrl}
              style={{
                ...imagePreviewImageStyle,
                display: imageLoading ? 'none' : 'block',
              }}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
              onLoad={() => {
                setImageLoading(false);
                setImageError(false);
              }}
            />
          )}
        </div>

        {/* Right side - Image URL Input and Buttons */}
        <div style={imagePreviewRightStyle}>
          <label htmlFor="imageUrl" style={inputLabelStyle}>
            Image URL
          </label>
          <input
            className="ncss-input pt2-sm pr3-sm pb2-sm pl3-sm"
            id="imageUrl"
            placeholder="https://example.com/image.jpg"
            style={imageUrlInputStyle}
            type="text"
            value={imageUrl}
            onChange={handleImageUrlChange}
          />
          <div style={buttonContainerStyle}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <input {...getInputProps()} style={hiddenInputStyle} />
            <ButtonWhite
              isDisabled={!uploadState.token || isUploading}
              label={isUploading ? 'Uploading...' : 'Upload'}
              style={{ color: '#111' }}
              onClick={() => open()}
            />
            <ButtonWhite
              label="Default"
              style={{ color: '#111' }}
              onClick={handleDefaultImage}
            />
          </div>
          {isUploading && (
            <div className="mt2-sm">
              <Loading />
            </div>
          )}
          {uploadState.uploadError && (
            <p className="text-color-error body-4 mt2-sm">{uploadState.uploadError}</p>
          )}
          {uploadState.uploadSuccess && (
            <p className="text-color-success body-4 mt2-sm">
              Successfully uploaded a new image. The Image URL field has been updated.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

StoreImage.propTypes = {
  accessToken: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  selectedStore: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
  storeData: PropTypes.shape({
    businessConcept: PropTypes.string,
    description: PropTypes.string,
    facilityType: PropTypes.string,
    imageUrl: PropTypes.string,
  }).isRequired,
  username: PropTypes.string.isRequired,
};

export default StoreImage;
